export type OpeningTimeLabels = {
  am: string;
  pm: string;
  invalid: string;
};

/**
 * Formats a Google opening time, stored as an HHMM number (930 = 09:30), as "9:30 AM".
 *
 * @param time The HHMM number.
 * @param labels The localized AM / PM suffixes and the text for a non-number.
 * @returns The 12-hour time.
 */
export function formatOpeningTime(
  time: unknown,
  labels: OpeningTimeLabels,
): string {
  if (typeof time !== "number" || isNaN(time)) {
    return labels.invalid;
  }

  const hours = Math.floor(time / 100);
  const minutes = time % 100;
  const amPm = hours >= 12 ? labels.pm : labels.am;
  const formattedHour = hours % 12 || 12;
  const formattedMinutes = minutes < 10 ? "0" + minutes : minutes.toString();

  return `${formattedHour}:${formattedMinutes} ${amPm}`;
}

/**
 * The venue's current day of the week (0 = Sunday), read in the venue's timezone.
 *
 * @param utcOffsetMinutes The venue's UTC offset, as the opening hours carry it.
 * @param now The moment to read; defaults to the current time.
 * @returns The day number Google uses in the opening hours.
 */
export function venueLocalDay(
  utcOffsetMinutes?: number | null,
  now: Date = new Date(),
): number {
  const venueNow = new Date(
    now.getTime() +
      now.getTimezoneOffset() * 60000 +
      (utcOffsetMinutes || 0) * 60000,
  );
  return venueNow.getDay();
}
